// Snow check-ins: first-hand snow cover and surface reports from signed-in users.
//
// This module is safe to import from Client Components. Validation, labels, rate-limit
// evaluation, reputation, evidence mapping and summary aggregation are pure. The two
// readers take a Supabase client from the caller (a service-role client on the server);
// nothing here creates a client or reads environment variables.
//
// Table, RLS, insert trigger and scoring RPC: supabase/migrations/20260926160200_snow_checkins.sql.
// Engine contract: docs/features/ski-scoring-v3.md sections 8 and 20 (CheckinEvidence in ski/types.ts).

import type { SupabaseClient } from "@supabase/supabase-js";
import type { AspectSector, CheckinEvidence, EvidenceTarget, SurfaceTag } from "./ski/types";
import { sha256Hex } from "./ski/math";
import { CONDITION_WRITE_MAX_BYTES, serialisedJsonBytes } from "./write-budget";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const SNOW_CHECKIN_BANDS = ["base", "mid", "summit"] as const;
export type SnowCheckinBand = (typeof SNOW_CHECKIN_BANDS)[number];

export const SNOW_CHECKIN_ASPECTS = ["N", "E", "S", "W"] as const satisfies readonly AspectSector[];

export const SNOW_CHECKIN_COVERS = ["full", "thin", "patchy", "bare"] as const;
export type SnowCheckinCover = (typeof SNOW_CHECKIN_COVERS)[number];

/** Display order for surface chips. Every SurfaceTag must appear (checked by test). */
export const SNOW_CHECKIN_SURFACES = [
  "powder", "packed", "groomed", "hard", "icy", "corn", "slush",
  "heavy", "chop", "moguls", "wind", "crust", "thin",
] as const satisfies readonly SurfaceTag[];

export const SNOW_CHECKIN_BAND_LABELS: Readonly<Record<SnowCheckinBand, string>> = {
  base: "Base",
  mid: "Mid",
  summit: "Summit",
};

export const SNOW_CHECKIN_COVER_LABELS: Readonly<Record<SnowCheckinCover, string>> = {
  full: "Full",
  thin: "Thin in places",
  patchy: "Patchy",
  bare: "Bare",
};

export const SNOW_CHECKIN_SURFACE_LABELS: Readonly<Record<SurfaceTag, string>> = {
  powder: "Powder",
  packed: "Packed",
  groomed: "Groomed",
  hard: "Hardpack",
  icy: "Icy",
  corn: "Spring corn",
  slush: "Slush",
  heavy: "Heavy",
  chop: "Chopped up",
  moguls: "Moguls",
  wind: "Wind affected",
  crust: "Crust",
  thin: "Thin snow",
};

// ---------------------------------------------------------------------------
// Limits and parameters
// ---------------------------------------------------------------------------

const HOUR_MS = 3_600_000;

/**
 * Submission limits. perLocationWindowMs, dailyWindowMs and dailyMax are duplicated in the
 * snow_checkins_before_insert trigger (the authority); keep both in sync.
 */
export const SNOW_CHECKIN_LIMITS = {
  perLocationWindowMs: 2 * HOUR_MS,
  dailyWindowMs: 24 * HOUR_MS,
  dailyMax: 10,
  observedPastMs: 12 * HOUR_MS,
  observedFutureSkewMs: 5 * 60_000,
  runIdMaxLength: 128,
  bodyMaxBytes: 2_048,
} as const;

/**
 * Reporter reputation, the temper on a check-in's likelihood (contract section 8) [E].
 * A new reporter gets 0.5. Each distinct UTC day with an accepted check-in in the 120 days
 * before this check-in adds 0.03, up to 0.8 at 10 days. "Accepted" means stored: every
 * stored row passed validation and the rate limits, and rows removed by account deletion
 * or moderation stop counting. Days, not rows, so a burst of reports on one day cannot buy
 * full weight. The window and cap are passed to read_snow_checkins_for_scoring.
 */
export const SNOW_CHECKIN_REPUTATION = {
  newReporter: 0.5, // [E] contract: 0.5 for a new user
  max: 0.8, // [E]
  historyDays: 120, // [E]
  historyCap: 10, // [E] active days for full reputation
} as const;

/** Bounds for the conditions-route reader (one RPC statement per location slice). */
export const SNOW_CHECKIN_READ = {
  lookbackMs: 36 * HOUR_MS,
  futureSkewMs: 5 * 60_000,
  maxLocations: 256,
  perLocationRows: 48,
  totalRows: 600,
  maxBytes: 256 * 1024,
  timeoutMs: 8_000,
} as const;

/** Public aggregated summary window and caps. */
export const SNOW_CHECKIN_SUMMARY = {
  windowMs: 12 * HOUR_MS,
  maxRows: 500,
  minRatingsForAverage: 3,
  /** Below this many reports only the total is public: no breakdown, rating count or time. */
  minReportsForBreakdown: 3,
  latestBucketMs: 15 * 60_000,
} as const;

export const SNOW_CHECKIN_EVIDENCE_SOURCE = "peakwave-checkin";
export const SNOW_CHECKIN_ORIGIN_PREFIX = "checkin:";
const ORIGIN_DOMAIN = "peakwave:snow-checkin:v1:";
const ORIGIN_KEYED_DOMAIN = "peakwave:snow-checkin:v1:keyed:";
const ORIGIN_HASH_HEX = 16;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Same character set as snow_checkins_run_id_check in the migration. */
const RUN_ID_PATTERN = /^[A-Za-z0-9_:./-]+$/;
/**
 * RFC 3339 date-time with an explicit offset. Without one, Date.parse reads a date-time in
 * the host time zone and a date-only string as midnight UTC, so both are rejected.
 */
const OBSERVED_AT_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d{1,9})?)?([Zz]|[+-]([01]\d|2[0-3]):[0-5]\d)$/;

export function isSnowCheckinUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (list as readonly string[]).includes(value);
}

export const isSnowCheckinBand = (v: unknown): v is SnowCheckinBand => isOneOf(SNOW_CHECKIN_BANDS, v);
export const isSnowCheckinCover = (v: unknown): v is SnowCheckinCover => isOneOf(SNOW_CHECKIN_COVERS, v);
export const isSnowCheckinSurface = (v: unknown): v is SurfaceTag => isOneOf(SNOW_CHECKIN_SURFACES, v);
export const isSnowCheckinAspect = (v: unknown): v is AspectSector => isOneOf(SNOW_CHECKIN_ASPECTS, v);

// ---------------------------------------------------------------------------
// Validation (pure)
// ---------------------------------------------------------------------------

export type SnowCheckinInput = Readonly<{
  locationId: string;
  runId: string | null;
  band: SnowCheckinBand | null;
  aspect: AspectSector | null;
  cover: SnowCheckinCover | null;
  surface: SurfaceTag | null;
  rating: number | null;
  /** ISO instant, or null for the server time. The request must include an offset (Z or +hh:mm). */
  observedAt: string | null;
}>;

export type SnowCheckinField =
  | "body" | "locationId" | "runId" | "band" | "aspect" | "cover" | "surface" | "rating"
  | "observedAt" | "report" | "target";

export type SnowCheckinValidation =
  | Readonly<{ ok: true; value: SnowCheckinInput }>
  | Readonly<{ ok: false; field: SnowCheckinField; message: string }>;

const fail = (field: SnowCheckinField, message: string): SnowCheckinValidation => ({ ok: false, field, message });
const absent = (value: unknown): value is null | undefined => value === null || value === undefined;

/**
 * Validates a POST body. Unknown keys (including any user id) are ignored: the author is
 * always the signed-in user. At least one of cover, surface or rating is required, and a
 * band or run is required so the report maps to a terrain unit.
 */
export function validateSnowCheckin(body: unknown, now: Date): SnowCheckinValidation {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return fail("body", "Send the snow report as a JSON object.");
  }
  const b = body as Record<string, unknown>;

  const locationId = b.locationId;
  if (!isSnowCheckinUuid(locationId)) return fail("locationId", "Choose a ski area for this report.");

  let runId: string | null = null;
  const rawRunId = b.runId;
  if (!absent(rawRunId)) {
    if (typeof rawRunId !== "string" || rawRunId.length === 0 || rawRunId.length > SNOW_CHECKIN_LIMITS.runIdMaxLength || !RUN_ID_PATTERN.test(rawRunId)) {
      return fail("runId", "That run could not be recognised.");
    }
    runId = rawRunId;
  }

  let band: SnowCheckinBand | null = null;
  if (!absent(b.band)) {
    if (!isSnowCheckinBand(b.band)) return fail("band", "Choose base, mid or summit.");
    band = b.band;
  }

  let aspect: AspectSector | null = null;
  if (!absent(b.aspect)) {
    if (!isSnowCheckinAspect(b.aspect)) return fail("aspect", "Choose a slope direction of N, E, S or W.");
    aspect = b.aspect;
  }

  let cover: SnowCheckinCover | null = null;
  if (!absent(b.cover)) {
    if (!isSnowCheckinCover(b.cover)) return fail("cover", "Choose full, thin in places, patchy or bare.");
    cover = b.cover;
  }

  let surface: SurfaceTag | null = null;
  if (!absent(b.surface)) {
    if (!isSnowCheckinSurface(b.surface)) return fail("surface", "Choose a surface from the list.");
    surface = b.surface;
  }

  let rating: number | null = null;
  const rawRating = b.rating;
  if (!absent(rawRating)) {
    if (typeof rawRating !== "number" || !Number.isInteger(rawRating) || rawRating < 0 || rawRating > 10) {
      return fail("rating", "Ratings are whole numbers from 0 to 10.");
    }
    rating = rawRating;
  }

  let observedAt: string | null = null;
  const rawObservedAt = b.observedAt;
  if (!absent(rawObservedAt)) {
    const ms = typeof rawObservedAt === "string" && OBSERVED_AT_PATTERN.test(rawObservedAt) ? Date.parse(rawObservedAt) : Number.NaN;
    if (!Number.isFinite(ms)) {
      return fail("observedAt", "Send the observation time as a date and time with a time zone, for example 2026-01-15T11:00:00Z.");
    }
    const nowMs = now.getTime();
    if (ms > nowMs + SNOW_CHECKIN_LIMITS.observedFutureSkewMs || ms < nowMs - SNOW_CHECKIN_LIMITS.observedPastMs) {
      return fail("observedAt", "Reports must describe the last 12 hours.");
    }
    observedAt = new Date(ms).toISOString();
  }

  if (cover === null && surface === null && rating === null) {
    return fail("report", "Add the snow cover, the surface or a rating.");
  }
  if (band === null && runId === null) {
    return fail("target", "Choose where on the mountain you are.");
  }

  return {
    ok: true,
    value: { locationId: locationId.toLowerCase(), runId, band, aspect, cover, surface, rating, observedAt },
  };
}

export type SnowCheckinInsertRow = {
  user_id: string;
  location_id: string;
  run_id: string | null;
  band: SnowCheckinBand | null;
  aspect: AspectSector | null;
  cover: SnowCheckinCover | null;
  surface: SurfaceTag | null;
  rating: number | null;
  observed_at?: string;
};

/** Insert payload. observed_at is omitted for "now" so the database default applies. */
export function snowCheckinInsertRow(input: SnowCheckinInput, userId: string): SnowCheckinInsertRow {
  return {
    user_id: userId,
    location_id: input.locationId,
    run_id: input.runId,
    band: input.band,
    aspect: input.aspect,
    cover: input.cover,
    surface: input.surface,
    rating: input.rating,
    ...(input.observedAt ? { observed_at: input.observedAt } : {}),
  };
}

// ---------------------------------------------------------------------------
// Rate limits (pure; the route reads the user's own last 24 hours in one query)
// ---------------------------------------------------------------------------

export type SnowCheckinRecentRow = Readonly<{ location_id: string; created_at: string }>;

export type SnowCheckinRateLimit =
  | Readonly<{ allowed: true }>
  | Readonly<{ allowed: false; reason: "location" | "daily"; retryAfterSeconds: number; message: string }>;

export const SNOW_CHECKIN_RATE_LIMIT_MESSAGES = {
  location: "You have already shared a snow report for this ski area in the last 2 hours. Please try again later.",
  daily: "You have shared 10 snow reports in the last 24 hours, which is the daily limit. Please try again later.",
} as const;

/**
 * Evaluates the user's own recent check-ins (any order). Rolling windows: one per location
 * per 2 hours and at most dailyMax per 24 hours.
 */
export function evaluateSnowCheckinRateLimit(
  rows: readonly SnowCheckinRecentRow[],
  locationId: string,
  now: Date,
): SnowCheckinRateLimit {
  const nowMs = now.getTime();
  const L = SNOW_CHECKIN_LIMITS;
  const times = rows
    .map((row) => ({ location: row.location_id.toLowerCase(), ms: Date.parse(row.created_at) }))
    .filter((row) => Number.isFinite(row.ms) && row.ms > nowMs - L.dailyWindowMs)
    .sort((a, b) => b.ms - a.ms);
  const target = locationId.toLowerCase();
  const here = times.find((row) => row.location === target && row.ms > nowMs - L.perLocationWindowMs);
  if (here) {
    return {
      allowed: false,
      reason: "location",
      retryAfterSeconds: Math.max(1, Math.ceil((here.ms + L.perLocationWindowMs - nowMs) / 1000)),
      message: SNOW_CHECKIN_RATE_LIMIT_MESSAGES.location,
    };
  }
  if (times.length >= L.dailyMax) {
    const releasing = times[L.dailyMax - 1];
    return {
      allowed: false,
      reason: "daily",
      retryAfterSeconds: Math.max(1, Math.ceil((releasing.ms + L.dailyWindowMs - nowMs) / 1000)),
      message: SNOW_CHECKIN_RATE_LIMIT_MESSAGES.daily,
    };
  }
  return { allowed: true };
}

// ---------------------------------------------------------------------------
// Reputation and origins (pure)
// ---------------------------------------------------------------------------

/** Reputation from prior active days ([E] rule documented on SNOW_CHECKIN_REPUTATION). */
export function snowCheckinReputation(priorActiveDays: number): number {
  const R = SNOW_CHECKIN_REPUTATION;
  if (!Number.isFinite(priorActiveDays) || priorActiveDays <= 0) return R.newReporter;
  const t = Math.min(Math.floor(priorActiveDays), R.historyCap) / R.historyCap;
  return Math.round((R.newReporter + (R.max - R.newReporter) * t) * 1000) / 1000;
}

/**
 * Pseudonymous evidence origin for a reporter: "checkin:" plus a 16-hex SHA-256 prefix of a
 * domain-separated user id. Raw user ids never appear in evidence. The engine keeps a
 * reporter's newest report per target per day, keyed by this origin.
 *
 * Without a secret the origin is unkeyed, so anyone who knows a user id (user_checkins rows
 * are publicly readable) can recompute it. Origins must therefore stay inside the server and
 * engine: never log, export, publish or return them from an API. Pass `secret` (a server-only
 * value) to make origins unlinkable without it. The 16-hex truncation also rules out length
 * extension on the secret-prefix construction. Changing or rotating the secret changes every
 * origin and therefore every check-in evidence key.
 */
export function snowCheckinOrigin(userId: string, secret?: string): string {
  const id = userId.trim().toLowerCase();
  const input = secret ? `${ORIGIN_KEYED_DOMAIN}${secret.length}:${secret}:${id}` : `${ORIGIN_DOMAIN}${id}`;
  return `${SNOW_CHECKIN_ORIGIN_PREFIX}${sha256Hex(input).slice(0, ORIGIN_HASH_HEX)}`;
}

// ---------------------------------------------------------------------------
// Scoring reader (bounded, one statement)
// ---------------------------------------------------------------------------

export type SnowCheckinScoringRow = Readonly<{
  locationId: string;
  /** Pseudonymous reporter key; the raw user id is dropped while parsing. */
  origin: string;
  runId: string | null;
  band: SnowCheckinBand | null;
  aspect: AspectSector | null;
  cover: SnowCheckinCover | null;
  surface: SurfaceTag | null;
  rating: number | null;
  observedAt: string;
  createdAt: string;
  priorActiveDays: number;
}>;

function isoOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** Parses RPC rows, hashing user ids into origins and dropping rows that fail validation. */
export function parseSnowCheckinScoringRows(data: unknown, originSecret?: string): SnowCheckinScoringRow[] {
  if (!Array.isArray(data)) return [];
  const out: SnowCheckinScoringRow[] = [];
  for (const item of data as unknown[]) {
    if (item === null || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    if (!isSnowCheckinUuid(r.location_id) || !isSnowCheckinUuid(r.user_id)) continue;
    const observedAt = isoOrNull(r.observed_at);
    const createdAt = isoOrNull(r.created_at);
    if (!observedAt || !createdAt) continue;
    const cover = isSnowCheckinCover(r.cover) ? r.cover : null;
    const surface = isSnowCheckinSurface(r.surface) ? r.surface : null;
    const rating = typeof r.rating === "number" && Number.isInteger(r.rating) && r.rating >= 0 && r.rating <= 10 ? r.rating : null;
    if (cover === null && surface === null && rating === null) continue;
    const runId = typeof r.run_id === "string" && r.run_id.length > 0 && r.run_id.length <= SNOW_CHECKIN_LIMITS.runIdMaxLength ? r.run_id : null;
    const days = typeof r.prior_active_days === "number" && Number.isFinite(r.prior_active_days) ? Math.max(0, Math.floor(r.prior_active_days)) : 0;
    out.push({
      locationId: r.location_id.toLowerCase(),
      origin: snowCheckinOrigin(r.user_id, originSecret),
      runId,
      band: isSnowCheckinBand(r.band) ? r.band : null,
      aspect: isSnowCheckinAspect(r.aspect) ? r.aspect : null,
      cover,
      surface,
      rating,
      observedAt,
      createdAt,
      priorActiveDays: days,
    });
  }
  return out;
}

export type SnowCheckinReadResult = Readonly<{
  byLocation: ReadonlyMap<string, readonly SnowCheckinScoringRow[]>;
  rows: number;
  /** Serialised bytes of the rows kept. */
  bytes: number;
  /** True when a location, row or byte cap may have hidden reports. */
  truncated: boolean;
  /** Recorded failure; the reader never throws. Absent evidence is "no evidence". */
  error: string | null;
}>;

export type SnowCheckinReadOptions = Readonly<{
  now: Date;
  /** Absolute epoch ms after which the reader must not start a request. */
  deadlineMs: number;
  /** Optional tighter byte budget for kept rows (clamped to 1 KiB..SNOW_CHECKIN_READ.maxBytes; non-finite values use the default). */
  maxBytes?: number;
  /** Optional server-only secret for keyed origins; see snowCheckinOrigin. */
  originSecret?: string;
}>;

const emptyRead = (error: string | null, truncated = false): SnowCheckinReadResult => ({
  byLocation: new Map(),
  rows: 0,
  bytes: 0,
  truncated,
  error,
});

/**
 * Reads the last 36 hours of check-ins for one slice of ski locations in a single RPC
 * statement, for the conditions route while it holds the conditions-io lease. Bounded by
 * location count, per-location rows, total rows, serialised bytes (256 KiB kept, 1 MiB hard
 * read cap) and a request timeout inside the caller's deadline. Failures are returned in
 * `error`, never thrown.
 */
export async function readSnowCheckinsForScoring(
  client: SupabaseClient,
  locationIds: readonly string[],
  options: SnowCheckinReadOptions,
): Promise<SnowCheckinReadResult> {
  const R = SNOW_CHECKIN_READ;
  const unique = [...new Set(locationIds.filter(isSnowCheckinUuid).map((id) => id.toLowerCase()))];
  const slice = unique.slice(0, R.maxLocations);
  const locationsCapped = unique.length > slice.length;
  if (slice.length === 0) return emptyRead(null);

  const remaining = options.deadlineMs - Date.now();
  if (!Number.isFinite(remaining) || remaining <= 0) return emptyRead("snow check-in read skipped: deadline reached", locationsCapped);

  const nowMs = options.now.getTime();
  let data: unknown;
  try {
    const response = await client
      .rpc("read_snow_checkins_for_scoring", {
        p_location_ids: slice,
        p_since: new Date(nowMs - R.lookbackMs).toISOString(),
        p_until: new Date(nowMs + R.futureSkewMs).toISOString(),
        p_per_location_limit: R.perLocationRows,
        p_total_limit: R.totalRows,
        p_history_days: SNOW_CHECKIN_REPUTATION.historyDays,
        p_history_cap: SNOW_CHECKIN_REPUTATION.historyCap,
      })
      .abortSignal(AbortSignal.timeout(Math.max(1, Math.min(R.timeoutMs, remaining))));
    if (response.error) return emptyRead(`snow check-in read failed: ${response.error.message}`, locationsCapped);
    data = response.data as unknown;
  } catch (error) {
    return emptyRead(`snow check-in read failed: ${error instanceof Error ? error.message : String(error)}`, locationsCapped);
  }

  const rawBytes = serialisedJsonBytes(data);
  if (rawBytes > CONDITION_WRITE_MAX_BYTES) {
    return emptyRead(`snow check-in read returned ${rawBytes} bytes, above the ${CONDITION_WRITE_MAX_BYTES} byte read budget`, true);
  }
  const rawCount = Array.isArray(data) ? data.length : 0;
  const parsed = parseSnowCheckinScoringRows(data, options.originSecret);

  const requestedBytes = typeof options.maxBytes === "number" && Number.isFinite(options.maxBytes) ? Math.floor(options.maxBytes) : R.maxBytes;
  const maxBytes = Math.min(R.maxBytes, Math.max(1024, requestedBytes));
  const byLocation = new Map<string, SnowCheckinScoringRow[]>();
  let bytes = 2;
  let kept = 0;
  let truncated = locationsCapped || rawCount >= R.totalRows;
  // Rows arrive newest first, so a byte or row cap drops the oldest reports.
  for (const row of parsed) {
    const rowBytes = serialisedJsonBytes(row) + 1;
    if (kept >= R.totalRows || bytes + rowBytes > maxBytes) {
      truncated = true;
      break;
    }
    bytes += rowBytes;
    kept++;
    const list = byLocation.get(row.locationId);
    if (list) list.push(row);
    else byLocation.set(row.locationId, [row]);
  }
  for (const list of byLocation.values()) if (list.length >= R.perLocationRows) truncated = true;

  return { byLocation, rows: kept, bytes, truncated, error: null };
}

// ---------------------------------------------------------------------------
// Evidence mapping (pure)
// ---------------------------------------------------------------------------

/**
 * Engine band index for a reported band. The terrain model has 3 bands (Base, Mid, Summit)
 * or 5 (Base, Lower, Mid, Upper, Summit): base is the lowest, summit the highest and mid
 * the middle band.
 */
export function snowCheckinBandIndex(band: SnowCheckinBand, bandCount: number): number {
  const n = Number.isInteger(bandCount) && bandCount >= 1 ? bandCount : 3;
  if (band === "base") return 0;
  if (band === "summit") return n - 1;
  return Math.floor((n - 1) / 2);
}

export type SnowCheckinEvidenceOptions = Readonly<{
  /** buildSkiTerrainModel(...).bands.length for the location. */
  bandCount: number;
  /**
   * Run ids the engine knows for this location. When given, a report on an unknown run
   * falls back to its band. When absent, a run id is trusted as the target.
   */
  knownRunIds?: ReadonlySet<string>;
}>;

/** Maps one stored check-in to v3 CheckinEvidence, or null when it has no usable target. */
export function snowCheckinToEvidence(row: SnowCheckinScoringRow, options: SnowCheckinEvidenceOptions): CheckinEvidence | null {
  if (!row.origin.startsWith(SNOW_CHECKIN_ORIGIN_PREFIX)) return null;
  const runUsable = row.runId !== null && (options.knownRunIds === undefined || options.knownRunIds.has(row.runId));
  let target: EvidenceTarget;
  if (runUsable && row.runId !== null) target = { runId: row.runId };
  else if (row.band !== null) {
    const band = snowCheckinBandIndex(row.band, options.bandCount);
    target = row.aspect ? { band, aspect: row.aspect } : { band };
  } else return null;

  const observedMs = Date.parse(row.observedAt);
  const createdMs = Date.parse(row.createdAt);
  if (!Number.isFinite(observedMs) || !Number.isFinite(createdMs)) return null;

  return {
    kind: "checkin",
    source: SNOW_CHECKIN_EVIDENCE_SOURCE,
    origin: row.origin,
    observedAt: new Date(observedMs).toISOString(),
    availableAt: new Date(Math.max(observedMs, createdMs)).toISOString(),
    target,
    cover: row.cover,
    surface: row.surface,
    rating: row.rating,
    reputation: snowCheckinReputation(row.priorActiveDays),
  };
}

export function snowCheckinsToEvidence(
  rows: readonly SnowCheckinScoringRow[],
  options: SnowCheckinEvidenceOptions,
): CheckinEvidence[] {
  const out: CheckinEvidence[] = [];
  for (const row of rows) {
    const evidence = snowCheckinToEvidence(row, options);
    if (evidence) out.push(evidence);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public aggregated summary (no identities)
// ---------------------------------------------------------------------------

export type SnowCheckinSummaryRow = Readonly<{
  cover: string | null;
  surface: string | null;
  rating: number | null;
  observed_at: string;
}>;

export type SnowCheckinPublicSummary = Readonly<{
  locationId: string;
  windowHours: number;
  total: number;
  cover: Readonly<Record<SnowCheckinCover, number>>;
  surface: Readonly<Record<SurfaceTag, number>>;
  rating: Readonly<{ count: number; average: number | null }>;
  /** Newest report time, floored to 15 minutes, or null. */
  latestObservedAt: string | null;
  /**
   * True when total is below SNOW_CHECKIN_SUMMARY.minReportsForBreakdown: cover and surface
   * counts are zero, the rating is { count: 0, average: null } and latestObservedAt is null,
   * so a quiet resort does not reveal one person's report.
   */
  breakdownWithheld: boolean;
  generatedAt: string;
  /** True when the row cap was reached, so counts are a lower bound. */
  truncated: boolean;
}>;

const zeroCounts = <K extends string>(keys: readonly K[]): Record<K, number> => {
  const out = {} as Record<K, number>;
  for (const k of keys) out[k] = 0;
  return out;
};

/**
 * Aggregates the last 12 hours of reports for display. Pure. Below
 * SNOW_CHECKIN_SUMMARY.minReportsForBreakdown reports only the total is returned.
 */
export function summariseSnowCheckins(
  locationId: string,
  rows: readonly SnowCheckinSummaryRow[],
  now: Date,
  truncated = false,
): SnowCheckinPublicSummary {
  const S = SNOW_CHECKIN_SUMMARY;
  const nowMs = now.getTime();
  const cover = zeroCounts(SNOW_CHECKIN_COVERS);
  const surface = zeroCounts(SNOW_CHECKIN_SURFACES);
  let total = 0;
  let ratingCount = 0;
  let ratingSum = 0;
  let latestMs = -Infinity;
  for (const row of rows) {
    const ms = Date.parse(row.observed_at);
    if (!Number.isFinite(ms) || ms < nowMs - S.windowMs || ms > nowMs + SNOW_CHECKIN_READ.futureSkewMs) continue;
    const c = isSnowCheckinCover(row.cover) ? row.cover : null;
    const s = isSnowCheckinSurface(row.surface) ? row.surface : null;
    const r = typeof row.rating === "number" && Number.isInteger(row.rating) && row.rating >= 0 && row.rating <= 10 ? row.rating : null;
    if (c === null && s === null && r === null) continue;
    total++;
    if (c) cover[c]++;
    if (s) surface[s]++;
    if (r !== null) {
      ratingCount++;
      ratingSum += r;
    }
    if (ms > latestMs) latestMs = ms;
  }
  const windowHours = S.windowMs / HOUR_MS;
  if (total < S.minReportsForBreakdown) {
    return {
      locationId: locationId.toLowerCase(),
      windowHours,
      total,
      cover: zeroCounts(SNOW_CHECKIN_COVERS),
      surface: zeroCounts(SNOW_CHECKIN_SURFACES),
      rating: { count: 0, average: null },
      latestObservedAt: null,
      breakdownWithheld: total > 0,
      generatedAt: now.toISOString(),
      truncated,
    };
  }
  const latest = Number.isFinite(latestMs) ? Math.floor(Math.min(latestMs, nowMs) / S.latestBucketMs) * S.latestBucketMs : null;
  return {
    locationId: locationId.toLowerCase(),
    windowHours,
    total,
    cover,
    surface,
    rating: {
      count: ratingCount,
      average: ratingCount >= S.minRatingsForAverage ? Math.round((ratingSum / ratingCount) * 10) / 10 : null,
    },
    latestObservedAt: latest === null ? null : new Date(latest).toISOString(),
    breakdownWithheld: false,
    generatedAt: now.toISOString(),
    truncated,
  };
}

/**
 * Reads and aggregates the public summary with a service-role client. Selects only
 * non-identifying columns. Throws on a database error so the caller can answer 503.
 */
export async function readSnowCheckinSummary(
  client: SupabaseClient,
  locationId: string,
  now: Date,
  signal?: AbortSignal,
): Promise<SnowCheckinPublicSummary> {
  const S = SNOW_CHECKIN_SUMMARY;
  const nowMs = now.getTime();
  const query = client
    .from("snow_checkins")
    .select("cover, surface, rating, observed_at")
    .eq("location_id", locationId.toLowerCase())
    .gte("observed_at", new Date(nowMs - S.windowMs).toISOString())
    .lte("observed_at", new Date(nowMs + SNOW_CHECKIN_READ.futureSkewMs).toISOString())
    .order("observed_at", { ascending: false })
    .limit(S.maxRows);
  const { data, error } = await (signal ? query.abortSignal(signal) : query);
  if (error) throw new Error(`snow check-in summary failed: ${error.message}`);
  const rows = Array.isArray(data) ? (data as unknown[]).filter(isSummaryRow) : [];
  return summariseSnowCheckins(locationId, rows, now, rows.length >= S.maxRows);
}

function isSummaryRow(value: unknown): value is SnowCheckinSummaryRow {
  if (value === null || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  return typeof r.observed_at === "string"
    && (r.cover === null || typeof r.cover === "string")
    && (r.surface === null || typeof r.surface === "string")
    && (r.rating === null || typeof r.rating === "number");
}
